import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck, Megaphone, MessageSquare, PenSquare, Reply } from 'lucide-react'

import { communicationService, type Announcement, type Message } from '../services/communicationService'
import { usePermissions } from '../hooks/usePermissions'
import { useAuthStore } from '../stores/authStore'
import MyNotifications from '../components/MyNotifications'
import { ClientRegister, StatusDot, type RegisterColumn } from '../components/register/ClientRegister'
import { DetailsModal } from '../components/register/DetailsModal'
import { MessageComposer, type MessageDraft } from '../components/communication/MessageComposer'
import { AnnouncementComposer, type AnnouncementDraft } from '../components/communication/AnnouncementComposer'
import {
  ANNOUNCEMENT_TYPES,
  AUDIENCES,
  announcementConfig,
  hasExpired,
  isIncoming,
  messageConfig,
  messageStanding,
  otherParty,
  priorityOf,
  publishedOn,
  replySubject,
} from '../components/communication/communicationRegisters'
import { apiErrorMessage } from '../utils/apiError'
import { todayIso } from '../utils/dates'

type Tab = 'messages' | 'announcements' | 'notifications'
const TABS: { id: Tab; label: string; icon: typeof Bell }[] = [
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'notifications', label: 'Notifications', icon: Bell },
]

const when = (value?: string | null) => (value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '')
const day = (value?: string | null) => (value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '')

type Composing = { kind: 'message'; replyTo?: Message } | { kind: 'announcement' } | null

export default function Communication() {
  const queryClient = useQueryClient()
  const { isStaff } = usePermissions()
  const userId = useAuthStore((state) => state.user?.id)
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const tab: Tab = TABS.some((entry) => entry.id === requested) ? (requested as Tab) : 'messages'
  const showTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams)
    params.set('tab', next)
    setSearchParams(params, { replace: true })
  }

  const [composing, setComposing] = useState<Composing>(null)
  const [openMessage, setOpenMessage] = useState<Message | null>(null)
  const [openAnnouncement, setOpenAnnouncement] = useState<Announcement | null>(null)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const today = todayIso()

  const messages = useQuery({ queryKey: ['communication', 'messages'], queryFn: () => communicationService.getMessages() })
  const announcements = useQuery({ queryKey: ['communication', 'announcements'], queryFn: () => communicationService.getAnnouncements() })
  // Only the composer needs the directory, and it can run to every account in the school.
  // Shared with the bell in the header.
  const unreadCounts = useQuery({ queryKey: ['communication', 'unread-counts'], queryFn: communicationService.getUnreadCounts })
  const recipients = useQuery({ queryKey: ['communication', 'recipients'], queryFn: communicationService.getRecipients, enabled: composing?.kind === 'message' })

  const refreshMessages = () => {
    void queryClient.invalidateQueries({ queryKey: ['communication', 'messages'] })
    void queryClient.invalidateQueries({ queryKey: ['communication', 'unread-counts'] })
  }
  const sendMessage = useMutation({
    mutationFn: (draft: MessageDraft) => communicationService.sendMessage({ ...draft, message_type: 'in_app' }),
    onSuccess: (sent) => {
      refreshMessages()
      setComposing(null)
      setOpenMessage(null)
      setNotice(`Message sent to ${sent.recipient_name || 'the recipient'}.`)
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'The message could not be sent. Check the recipient and try again.')),
  })
  const publish = useMutation({
    mutationFn: (draft: AnnouncementDraft) => communicationService.createAnnouncement(draft),
    onSuccess: (published) => {
      void queryClient.invalidateQueries({ queryKey: ['communication', 'announcements'] })
      setComposing(null)
      showTab('announcements')
      setNotice(`“${published.title}” is published to ${AUDIENCES[published.target_audience]?.toLowerCase() ?? 'the school'}.`)
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'The announcement could not be published.')),
  })
  const readMessage = useMutation({ mutationFn: communicationService.markMessageAsRead, onSuccess: refreshMessages })
  const setOnSignIn = useMutation({
    mutationFn: communicationService.setAnnouncementOnSignIn,
    onSuccess: (updated) => {
      setOpenAnnouncement(updated)
      void queryClient.invalidateQueries({ queryKey: ['communication', 'announcements'] })
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'The sign-in page setting could not be changed.')),
  })
  const readAll = useMutation({
    mutationFn: () => communicationService.markAllMessagesAsRead(),
    onSuccess: () => {
      refreshMessages()
      setNotice('Every message is marked as read.')
    },
  })

  const unread = messages.data?.results.filter((message) => isIncoming(message, userId) && !message.read_at).length ?? 0
  const messageRegister = useMemo(() => messageConfig(userId), [userId])
  const announcementRegister = useMemo(() => announcementConfig(today), [today])

  const open = (message: Message) => {
    setOpenMessage(message)
    if (isIncoming(message, userId) && !message.read_at) readMessage.mutate(message.id)
  }
  // A notification links here as ?message=12 or ?announcement=5: open that one, then drop the id
  // from the address so closing it does not bring it back.
  const linkedMessage = searchParams.get('message')
  const linkedAnnouncement = searchParams.get('announcement')
  const messageRows = messages.data?.results
  const announcementRows = announcements.data?.results
  useEffect(() => {
    if (!linkedMessage || !messageRows) return
    const found = messageRows.find((message) => String(message.id) === linkedMessage)
    const params = new URLSearchParams(searchParams)
    params.delete('message')
    setSearchParams(params, { replace: true })
    if (!found) {
      setError('That message is no longer in your messages.')
      return
    }
    setOpenMessage(found)
    if (found.recipient === userId && !found.read_at) readMessage.mutate(found.id)
    // Runs when a link arrives or the list first loads; the rest are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedMessage, messageRows])
  useEffect(() => {
    if (!linkedAnnouncement || !announcementRows) return
    const found = announcementRows.find((item) => String(item.id) === linkedAnnouncement)
    const params = new URLSearchParams(searchParams)
    params.delete('announcement')
    setSearchParams(params, { replace: true })
    if (found) setOpenAnnouncement(found)
    else setError('That announcement is no longer shown to you.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAnnouncement, announcementRows])

  const compose = (next: Composing) => {
    setError('')
    setNotice('')
    setComposing(next)
  }

  const messageColumns: RegisterColumn<Message>[] = [
    { header: 'Status', cell: (message) => <StatusDot {...messageStanding(message, userId)} />, exportValue: (message) => messageStanding(message, userId).label },
    { header: 'With', cell: (message) => <span className="text-gray-700"><span className="text-gray-400">{isIncoming(message, userId) ? 'From ' : 'To '}</span>{otherParty(message, userId).name}</span>, exportValue: (message) => `${isIncoming(message, userId) ? 'From' : 'To'} ${otherParty(message, userId).name}` },
    {
      header: 'Subject',
      cell: (message) => <span className={isIncoming(message, userId) && !message.read_at ? 'font-semibold text-gray-900' : 'text-gray-900'}>{message.subject}</span>,
      exportValue: (message) => message.subject,
      className: 'max-w-[16rem] truncate',
    },
    { header: 'Message', cell: (message) => message.content, exportValue: (message) => message.content, className: 'max-w-[22rem] truncate text-gray-500' },
    { header: 'Sent', cell: (message) => when(message.created_at), exportValue: (message) => when(message.created_at), className: 'tabular-nums text-gray-600' },
  ]

  const announcementColumns: RegisterColumn<Announcement>[] = [
    { header: 'Title', cell: (item) => <span className="font-medium text-gray-900">{item.title}</span>, exportValue: (item) => item.title, className: 'max-w-[18rem] truncate' },
    { header: 'Priority', cell: (item) => <StatusDot {...priorityOf(item)} />, exportValue: (item) => priorityOf(item).label },
    { header: 'Audience', cell: (item) => AUDIENCES[item.target_audience] ?? item.target_audience, exportValue: (item) => AUDIENCES[item.target_audience] ?? item.target_audience },
    { header: 'Kind', cell: (item) => ANNOUNCEMENT_TYPES[item.announcement_type] ?? item.announcement_type, exportValue: (item) => ANNOUNCEMENT_TYPES[item.announcement_type] ?? item.announcement_type, className: 'text-gray-600' },
    { header: 'By', cell: (item) => item.author_name || '—', exportValue: (item) => item.author_name ?? '', className: 'text-gray-600' },
    { header: 'Published', cell: (item) => day(publishedOn(item)), exportValue: (item) => publishedOn(item).slice(0, 10), className: 'tabular-nums' },
    {
      header: 'Shown until',
      cell: (item) => (item.expires_at ? <span className={hasExpired(item, today) ? 'text-gray-400' : undefined}>{day(item.expires_at)}</span> : <span className="text-gray-400">No end</span>),
      exportValue: (item) => item.expires_at?.slice(0, 10) ?? '',
      className: 'tabular-nums',
    },
  ]

  const replyFor = (message: Message) => {
    const other = otherParty(message, userId)
    return { id: message.id, recipient: other, subject: replySubject(message.subject), quote: message.content, from: message.sender_name || 'They', sent: when(message.created_at) }
  }
  const inReplyTo = openMessage?.reply_to ? messages.data?.results.find((message) => message.id === openMessage.reply_to) : undefined

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communication</h1>
          <p className="text-gray-600">Direct messages, school announcements and your notifications in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => compose({ kind: 'message' })} className="btn btn-primary">
            <PenSquare className="mr-2 h-4 w-4" />New message
          </button>
          {isStaff && (
            <button type="button" onClick={() => compose({ kind: 'announcement' })} className="btn btn-outline">
              <Megaphone className="mr-2 h-4 w-4" />New announcement
            </button>
          )}
        </div>
      </div>

      <nav aria-label="Communication" className="flex flex-wrap gap-x-1 border-b border-gray-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => showTab(id)}
            aria-current={tab === id ? 'page' : undefined}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}
          >
            <Icon className="h-4 w-4" />
            {label}
            {id === 'messages' && unread > 0 && <span className="rounded-full bg-primary-600 px-1.5 text-xs font-semibold text-white tabular-nums">{unread}</span>}
            {id === 'notifications' && !!unreadCounts.data?.unread_notifications && (
              <span className="rounded-full bg-primary-600 px-1.5 text-xs font-semibold text-white tabular-nums">{unreadCounts.data.unread_notifications}</span>
            )}
          </button>
        ))}
      </nav>

      {error && !composing && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="text-xs font-medium underline">Dismiss</button>
        </div>
      )}

      {notice && (
        <div role="status" className="flex items-center justify-between gap-3 rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="text-xs font-medium underline">Dismiss</button>
        </div>
      )}

      {tab === 'messages' && (
        <ClientRegister
          title="Messages"
          description="Messages you have sent and received. Click one to read it in full and reply."
          actions={unread > 0 ? (
            <button type="button" onClick={() => readAll.mutate()} disabled={readAll.isPending} className="pager-button h-8">
              <CheckCheck className="h-3.5 w-3.5" />Mark {unread} as read
            </button>
          ) : undefined}
          rows={messages.data?.results}
          config={messageRegister}
          columns={messageColumns}
          rowKey={(message) => message.id}
          onOpen={open}
          openLabel={() => 'Read'}
          noun={['message', 'messages']}
          exportName={{ file: 'messages', title: 'Messages' }}
          isLoading={messages.isLoading}
          isError={messages.isError}
          onRetry={() => void messages.refetch()}
          emptyText="No messages yet. Use New message to write the first one."
        />
      )}

      {tab === 'announcements' && (
        <ClientRegister
          title="Announcements"
          description={isStaff ? 'Everything published to the school. Click one to read it in full.' : 'Announcements from the school. Click one to read it in full.'}
          rows={announcements.data?.results}
          config={announcementRegister}
          columns={announcementColumns}
          rowKey={(item) => item.id}
          onOpen={setOpenAnnouncement}
          openLabel={() => 'Read'}
          noun={['announcement', 'announcements']}
          exportName={{ file: 'announcements', title: 'Announcements' }}
          isLoading={announcements.isLoading}
          isError={announcements.isError}
          onRetry={() => void announcements.refetch()}
          emptyText={isStaff ? 'Nothing published yet. Use New announcement to inform the school.' : 'New school announcements will appear here.'}
        />
      )}

      {tab === 'notifications' && <MyNotifications />}

      {openMessage && (
        <DetailsModal
          title={openMessage.subject}
          headline={{
            label: isIncoming(openMessage, userId) ? 'From' : 'To',
            value: otherParty(openMessage, userId).name,
            note: when(openMessage.created_at),
          }}
          status={messageStanding({ ...openMessage, read_at: openMessage.read_at || (isIncoming(openMessage, userId) ? openMessage.created_at : null) }, userId)}
          facts={[
            ['From', openMessage.sender_name],
            ['To', openMessage.recipient_name],
            ['Sent', when(openMessage.created_at)],
            ['Read', isIncoming(openMessage, userId) ? when(openMessage.read_at) || 'Just now' : when(openMessage.read_at) || 'Not yet'],
            ...(inReplyTo ? ([['In reply to', inReplyTo.subject]] as [string, string][]) : []),
          ]}
          sections={[{ title: 'Message', body: openMessage.content }]}
          onClose={() => setOpenMessage(null)}
          footer={
            <>
              <button type="button" onClick={() => setOpenMessage(null)} className="btn-outline">Close</button>
              <button
                type="button"
                onClick={() => {
                  compose({ kind: 'message', replyTo: openMessage })
                  setOpenMessage(null)
                }}
                className="btn btn-primary"
              >
                <Reply className="mr-2 h-4 w-4" />{isIncoming(openMessage, userId) ? 'Reply' : 'Write again'}
              </button>
            </>
          }
        />
      )}

      {openAnnouncement && (
        <DetailsModal
          title={openAnnouncement.title}
          headline={{ label: `For ${(AUDIENCES[openAnnouncement.target_audience] ?? 'everyone').toLowerCase()}`, value: ANNOUNCEMENT_TYPES[openAnnouncement.announcement_type] ?? 'Announcement', note: `Published ${when(publishedOn(openAnnouncement))}` }}
          status={priorityOf(openAnnouncement)}
          facts={[
            ['Audience', AUDIENCES[openAnnouncement.target_audience]],
            ['Priority', priorityOf(openAnnouncement).label],
            ['Published by', openAnnouncement.author_name],
            ['Published', when(publishedOn(openAnnouncement))],
            ['Shown until', openAnnouncement.expires_at ? `${day(openAnnouncement.expires_at)}${hasExpired(openAnnouncement, today) ? ' (expired)' : ''}` : 'No end date'],
          ]}
          sections={[{ title: 'Announcement', body: openAnnouncement.content }]}
          onClose={() => setOpenAnnouncement(null)}
        >
          {isStaff && (
            <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!openAnnouncement.show_on_sign_in}
                disabled={setOnSignIn.isPending || hasExpired(openAnnouncement, today)}
                onChange={(event) => setOnSignIn.mutate({ id: openAnnouncement.id, show: event.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
              />
              <span>
                <span className="font-medium text-gray-900">Show on the sign-in page</span>
                <span className="block text-xs text-gray-500">
                  {hasExpired(openAnnouncement, today)
                    ? 'This announcement has expired, so it no longer shows there.'
                    : 'Anyone can see the sign-in page, without an account. It comes off by itself when the announcement expires.'}
                </span>
              </span>
            </label>
          )}
        </DetailsModal>
      )}

      {composing?.kind === 'message' && (
        <MessageComposer
          recipients={recipients.data}
          recipientsLoading={recipients.isLoading}
          replyTo={composing.replyTo ? replyFor(composing.replyTo) : undefined}
          pending={sendMessage.isPending}
          error={error}
          onSend={(draft) => {
            setError('')
            sendMessage.mutate(draft)
          }}
          onClose={() => setComposing(null)}
        />
      )}
      {composing?.kind === 'announcement' && (
        <AnnouncementComposer
          pending={publish.isPending}
          error={error}
          onPublish={(draft) => {
            setError('')
            publish.mutate(draft)
          }}
          onClose={() => setComposing(null)}
        />
      )}
    </div>
  )
}
